/**
 * GeofenceEditor — self-controlled click-to-draw polygon editor (DISPATCH-04 / GEO-02).
 *
 * Quick 260615-bs7 (#1) — mapbox-gl-draw never reliably registered click events
 * for vertex placement under mapbox-gl v3 (draw mode activated, dragPan was even
 * forced off, but clicks still didn't drop points). We abandon mapbox-gl-draw
 * entirely and own the interaction:
 *   - Enter "Draw polygon" → clear ring, disable dragPan + doubleClickZoom.
 *   - Each map click appends a vertex.
 *   - Clicking the FIRST vertex (≥3 points) OR the "Close polygon" button closes
 *     the ring; pan/zoom are re-enabled.
 *   - The ring is rendered via a controlled GeoJSON source (fill + outline +
 *     vertex circles) we add ourselves and re-add on style switches.
 *   - "Verify & Save" / "Save Draft" POST the closed ring via
 *     PUT /v1/sites/:id/geofence (unchanged geojson helpers + useSaveGeofence).
 *
 * CSS imports for mapbox-gl are in main.tsx (RESEARCH Pitfall 1).
 *
 * Spec: 09-06-PLAN.md; 09-UI-SPEC.md §6; DISPATCH-04 / GEO-02.
 */

import * as React from 'react';
import { useEffect, useRef, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import { Pen, Check, Trash2, Undo2 } from 'lucide-react';
import { useSharedMap } from '../map/MapProvider.js';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { useGeofence, useSaveGeofence, type SaveGeofenceBody } from './use-geofence.js';
import { MAP_COLOURS } from '../../lib/map-colours';
import {
  isClosedRingPolygon,
  featureToString,
  ringFromGeofence,
  toClosedRingFeature,
} from './geojson.js';
import { formatNumber } from '@lodex-pro/shared/number-format';

const MAPBOX_TOKEN = import.meta.env['VITE_MAPBOX_TOKEN'] as string | undefined;

const DEFAULT_ZOOM = 9;

const MAP_STYLES: ReadonlyArray<{ key: string; label: string; url: string }> = [
  { key: 'satellite', label: 'Satellite', url: 'mapbox://styles/mapbox/satellite-streets-v12' },
  { key: 'streets', label: 'Streets', url: 'mapbox://styles/mapbox/streets-v12' },
  { key: 'dark', label: 'Dark', url: 'mapbox://styles/mapbox/dark-v11' },
];
const DEFAULT_STYLE_KEY = 'satellite';

const SRC = 'gf-draw';
const RADIUS_SRC = 'gf-radius'; // controlled source for the RADIUS-mode ring
const CLOSE_PX = 14; // click-distance to the first vertex that closes the ring

const MIN_RADIUS_FT = 100;
const MAX_RADIUS_FT = 2640; // half a mile
const DEFAULT_RADIUS_FT = 300; // platform default — pickup (§15)
const DEFAULT_DISPOSAL_RADIUS_FT = 500; // platform default — disposal (§15)
const FT_PER_M = 3.28084;

type Pt = [number, number];

/**
 * Phase 8 / Task 8.2 (DEC-080) — build a ~64-point circle Polygon FeatureCollection
 * around a center [lng,lat] with a radius in FEET, for the RADIUS-mode ring + a
 * center Point. Uses the equirectangular small-distance approximation (geofence
 * radii are ≤ half a mile, so distortion is negligible).
 */
function buildRadiusCollection(center: Pt, radiusFt: number): GeoJSON.FeatureCollection {
  const [lng, lat] = center;
  const radiusM = radiusFt / FT_PER_M;
  const dLat = (radiusM / 6378137) * (180 / Math.PI);
  const dLng = dLat / Math.cos((lat * Math.PI) / 180);
  const ring: Pt[] = [];
  const steps = 64;
  for (let i = 0; i <= steps; i++) {
    const theta = (i / steps) * 2 * Math.PI;
    ring.push([lng + dLng * Math.cos(theta), lat + dLat * Math.sin(theta)]);
  }
  return {
    type: 'FeatureCollection',
    features: [
      { type: 'Feature', geometry: { type: 'Polygon', coordinates: [ring] }, properties: {} },
      { type: 'Feature', geometry: { type: 'Point', coordinates: center }, properties: {} },
    ],
  };
}

export interface GeofenceEditorProps {
  readonly siteId: string | null;
  readonly siteName?: string | undefined;
  readonly centerLat?: number | null | undefined;
  readonly centerLng?: number | null | undefined;
  // Phase 8 / Task 8.1 (disposal) — PICKUP (default) or DISPOSAL. Drives the
  // geofence GET/PUT site_type and the default radius (§15: 300 pickup / 500 disposal).
  readonly siteType?: 'PICKUP' | 'DISPOSAL' | 'TANK_BATTERY' | undefined;
}

export function GeofenceEditor(props: GeofenceEditorProps): React.JSX.Element {
  if (!MAPBOX_TOKEN) {
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--color-bg-secondary)',
          color: 'var(--color-text-secondary)',
          fontSize: '14px',
          fontFamily: 'var(--font-sans, system-ui, sans-serif)',
        }}
      >
        <p>Map unavailable — check your Mapbox token or network connection.</p>
      </div>
    );
  }
  return <GeofenceEditorInner {...props} />;
}

// ---------------------------------------------------------------------------
// Controlled-source render helpers
// ---------------------------------------------------------------------------

function buildFeatureCollection(verts: Pt[], closed: boolean): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  const first = verts[0];
  if (verts.length >= 3 && closed && first) {
    features.push({
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: [[...verts, first]] },
      properties: {},
    });
  } else if (verts.length >= 2) {
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: verts },
      properties: {},
    });
  }
  for (const c of verts) {
    features.push({ type: 'Feature', geometry: { type: 'Point', coordinates: c }, properties: {} });
  }
  return { type: 'FeatureCollection', features };
}

function removeLayers(map: mapboxgl.Map): void {
  for (const id of [`${SRC}-fill`, `${SRC}-line`, `${SRC}-vertex`]) {
    if (map.getLayer(id)) map.removeLayer(id);
  }
  if (map.getSource(SRC)) map.removeSource(SRC);
}

function ensureLayers(map: mapboxgl.Map): void {
  if (map.getSource(SRC)) return;
  map.addSource(SRC, {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
  });
  map.addLayer({
    id: `${SRC}-fill`,
    type: 'fill',
    source: SRC,
    filter: ['==', '$type', 'Polygon'],
    paint: { 'fill-color': MAP_COLOURS.accent, 'fill-opacity': 0.2 },
  });
  map.addLayer({
    id: `${SRC}-line`,
    type: 'line',
    source: SRC,
    filter: ['in', '$type', 'Polygon', 'LineString'],
    paint: { 'line-color': MAP_COLOURS.accent, 'line-width': 2 },
  });
  map.addLayer({
    id: `${SRC}-vertex`,
    type: 'circle',
    source: SRC,
    filter: ['==', '$type', 'Point'],
    paint: {
      'circle-radius': 6,
      'circle-color': MAP_COLOURS.paper,
      'circle-stroke-color': MAP_COLOURS.accent,
      'circle-stroke-width': 2,
    },
  });
}

// Phase 8 / Task 8.2 (DEC-080) — controlled source + layers for the RADIUS ring.
function ensureRadiusLayers(map: mapboxgl.Map): void {
  if (map.getSource(RADIUS_SRC)) return;
  map.addSource(RADIUS_SRC, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  map.addLayer({
    id: `${RADIUS_SRC}-fill`,
    type: 'fill',
    source: RADIUS_SRC,
    filter: ['==', '$type', 'Polygon'],
    paint: { 'fill-color': MAP_COLOURS.accent, 'fill-opacity': 0.15 },
  });
  map.addLayer({
    id: `${RADIUS_SRC}-line`,
    type: 'line',
    source: RADIUS_SRC,
    filter: ['==', '$type', 'Polygon'],
    paint: { 'line-color': MAP_COLOURS.accent, 'line-width': 2 },
  });
}

function removeRadiusLayers(map: mapboxgl.Map): void {
  for (const id of [`${RADIUS_SRC}-fill`, `${RADIUS_SRC}-line`]) {
    if (map.getLayer(id)) map.removeLayer(id);
  }
  if (map.getSource(RADIUS_SRC)) map.removeSource(RADIUS_SRC);
}

function GeofenceEditorInner({
  siteId,
  siteName,
  centerLat,
  centerLng,
  siteType = 'PICKUP',
}: GeofenceEditorProps): React.JSX.Element {
  // §15 platform default radius differs by site type (300 pickup / 500 disposal).
  const defaultRadiusFt = siteType === 'DISPOSAL' ? DEFAULT_DISPOSAL_RADIUS_FT : DEFAULT_RADIUS_FT;
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);

  // Wave 2 (#1) — shared, session-lived map instance.
  const { acquire, release } = useSharedMap();
  const { data: geofenceData } = useGeofence(siteId, siteType);
  const { mutate: saveGeofence, isPending: isSaving } = useSaveGeofence(siteId ?? '', siteType);

  const [vertices, setVertices] = useState<Pt[]>([]);
  const [mode, setMode] = useState<'idle' | 'drawing'>('idle');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [drawError, setDrawError] = useState<string | null>(null);
  const [styleKey, setStyleKey] = useState<string>(DEFAULT_STYLE_KEY);
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);

  // Phase 8 / Task 8.2 (DEC-080) — RADIUS is the default geometry mode; POLYGON is
  // the optional per-site upgrade (the existing draw flow).
  const [geometryMode, setGeometryMode] = useState<'RADIUS' | 'POLYGON'>('RADIUS');
  const [radiusFt, setRadiusFt] = useState<number>(DEFAULT_RADIUS_FT);
  // The draggable pin position; seeded from the site center, persisted on save.
  const [pin, setPin] = useState<Pt | null>(null);

  // Refs mirror state for the once-registered map click handler (avoid stale closures).
  const verticesRef = useRef<Pt[]>(vertices);
  const modeRef = useRef<'idle' | 'drawing'>(mode);
  const geometryModeRef = useRef<'RADIUS' | 'POLYGON'>(geometryMode);
  const radiusFtRef = useRef<number>(radiusFt);
  const pinRef = useRef<Pt | null>(pin);
  const markerRef = useRef<mapboxgl.Marker | null>(null);
  const styleInitedRef = useRef(false);
  useEffect(() => {
    geometryModeRef.current = geometryMode;
  }, [geometryMode]);
  useEffect(() => {
    radiusFtRef.current = radiusFt;
  }, [radiusFt]);
  useEffect(() => {
    pinRef.current = pin;
  }, [pin]);
  useEffect(() => {
    verticesRef.current = vertices;
  }, [vertices]);
  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  const renderData = useCallback((): void => {
    const map = mapRef.current;
    if (!map) return;
    const src = map.getSource<mapboxgl.GeoJSONSource>(SRC);
    if (!src) return;
    // Phase 8 fix — the polygon source renders ONLY in POLYGON mode. Otherwise a
    // polygon drawn before "Switch to radius" stayed visible under the radius ring.
    src.setData(
      geometryModeRef.current === 'POLYGON'
        ? buildFeatureCollection(verticesRef.current, modeRef.current === 'idle')
        : { type: 'FeatureCollection', features: [] },
    );
  }, []);

  // Re-render the polygon source whenever the ring, draw mode, OR geometry mode
  // changes (the last so a mode switch clears/shows the polygon overlay).
  useEffect(() => {
    renderData();
  }, [vertices, mode, geometryMode, renderData]);

  // Phase 8 / Task 8.2 — RADIUS ring controlled source.
  const renderRadius = useCallback((): void => {
    const map = mapRef.current;
    if (!map) return;
    const src = map.getSource<mapboxgl.GeoJSONSource>(RADIUS_SRC);
    if (!src) return;
    const center = pinRef.current;
    if (geometryModeRef.current === 'RADIUS' && center) {
      src.setData(buildRadiusCollection(center, radiusFtRef.current));
    } else {
      src.setData({ type: 'FeatureCollection', features: [] });
    }
  }, []);

  useEffect(() => {
    renderRadius();
  }, [pin, radiusFt, geometryMode, renderRadius]);

  // Seed the pin from the selected site's center on every site change. The
  // existing-geofence load effect (below) overrides geometry/radius when a saved
  // geofence exists.
  useEffect(() => {
    setPin(centerLng != null && centerLat != null ? [centerLng, centerLat] : null);
    setGeometryMode('RADIUS');
    setRadiusFt(defaultRadiusFt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  // Draggable center pin (RADIUS mode only). Drag → move tank-battery location.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (geometryMode === 'RADIUS' && pin) {
      if (markerRef.current === null) {
        const m = new mapboxgl.Marker({ color: MAP_COLOURS.accent, draggable: true });
        m.on('dragend', () => {
          const ll = m.getLngLat();
          setPin([ll.lng, ll.lat]);
        });
        m.setLngLat(pin).addTo(map);
        markerRef.current = m;
      } else {
        markerRef.current.setLngLat(pin);
      }
    } else if (markerRef.current !== null) {
      markerRef.current.remove();
      markerRef.current = null;
    }
  }, [geometryMode, pin]);

  const enablePan = useCallback((on: boolean): void => {
    const map = mapRef.current;
    if (!map) return;
    if (on) {
      map.dragPan.enable();
      map.doubleClickZoom.enable();
    } else {
      map.dragPan.disable();
      map.doubleClickZoom.disable();
    }
  }, []);

  const closePolygon = useCallback((): void => {
    if (verticesRef.current.length < 3) return;
    setMode('idle');
    enablePan(true);
  }, [enablePan]);

  // ── Attach the SHARED map (Wave 2 #1) ─────────────────────────────────────
  // Mount once: attach the session map, set the satellite base style, wire the
  // self-controlled draw, and add the controlled source/layers. On unmount, tear
  // down ONLY this surface's listeners + source/layers and park the shared map
  // (it is never .remove()d here — DriverMap re-attaches the same instance).
  useEffect(() => {
    if (!containerRef.current) return;
    const map = acquire(containerRef.current);
    if (!map) return;
    mapRef.current = map;

    const initialStyle =
      MAP_STYLES.find((s) => s.key === styleKey)?.url ??
      'mapbox://styles/mapbox/satellite-streets-v12';
    map.setStyle(initialStyle);

    const onMouseMove = (e: mapboxgl.MapMouseEvent): void => {
      setCoords({ lat: e.lngLat.lat, lng: e.lngLat.lng });
    };
    const onMouseOut = (): void => {
      setCoords(null);
    };
    const onError = (e: { error?: { message?: string } }): void => {
      setDrawError(`Map error: ${e.error?.message ?? 'failed to load map style or tiles'}`);
    };
    const onClick = (e: mapboxgl.MapMouseEvent): void => {
      if (modeRef.current !== 'drawing') return;
      const verts = verticesRef.current;
      if (verts.length >= 3) {
        const first = map.project(verts[0] as mapboxgl.LngLatLike);
        const dx = first.x - e.point.x;
        const dy = first.y - e.point.y;
        if (Math.sqrt(dx * dx + dy * dy) <= CLOSE_PX) {
          closePolygon();
          return;
        }
      }
      setVertices([...verts, [e.lngLat.lng, e.lngLat.lat]]);
    };
    const onStyleReady = (): void => {
      ensureLayers(map);
      ensureRadiusLayers(map);
      renderData();
      renderRadius();
    };

    map.on('mousemove', onMouseMove);
    map.on('mouseout', onMouseOut);
    map.on('error', onError);
    map.on('click', onClick);
    map.on('load', onStyleReady);
    // Re-add the controlled source/layers after a base-style switch (#4).
    map.on('style.load', onStyleReady);
    // If the style is already loaded (shared map was warm), wire layers now.
    if (map.isStyleLoaded()) onStyleReady();
    window.setTimeout(() => {
      map.resize();
    }, 0);

    return () => {
      map.off('mousemove', onMouseMove);
      map.off('mouseout', onMouseOut);
      map.off('error', onError);
      map.off('click', onClick);
      map.off('load', onStyleReady);
      map.off('style.load', onStyleReady);
      try {
        removeLayers(map);
        removeRadiusLayers(map);
      } catch {
        /* style may be mid-reload; layers re-cleared on next attach */
      }
      if (markerRef.current !== null) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      enablePan(true);
      mapRef.current = null;
      release();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acquire, release]);

  // Recenter on the selected site without re-creating the map (#1).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (centerLng != null && centerLat != null) {
      map.flyTo({ center: [centerLng, centerLat], zoom: DEFAULT_ZOOM });
    }
  }, [siteId, centerLat, centerLng]);

  // ── Base-style switch (#4) ────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!styleInitedRef.current) {
      styleInitedRef.current = true;
      return;
    }
    const url = MAP_STYLES.find((s) => s.key === styleKey)?.url;
    if (url) map.setStyle(url); // 'style.load' re-adds the controlled source + re-renders
  }, [styleKey]);

  // ── Load existing geofence — geofence_type drives which mode opens (DEC-080) ──
  useEffect(() => {
    const gf = geofenceData?.geofence;
    if (!gf) return;
    if (gf.geofenceType === 'POLYGON' && gf.polygonGeojson) {
      const ring = ringFromGeofence(gf.polygonGeojson);
      if (ring.length < 3) return;
      const a = ring[0];
      const b = ring[ring.length - 1];
      const closed = a != null && b != null && a[0] === b[0] && a[1] === b[1];
      setVertices(closed ? ring.slice(0, -1) : ring);
      setMode('idle');
      setGeometryMode('POLYGON');
    } else if (gf.geofenceType === 'RADIUS') {
      setGeometryMode('RADIUS');
      if (typeof gf.radiusFt === 'number') setRadiusFt(gf.radiusFt);
    }
  }, [geofenceData]);

  // ── Toolbar handlers ──────────────────────────────────────────────────────
  const handleStartDraw = useCallback((): void => {
    setVertices([]);
    setMode('drawing');
    setSaveError(null);
    setSaveSuccess(false);
    enablePan(false);
  }, [enablePan]);

  const handleUndo = useCallback((): void => {
    setVertices((v) => v.slice(0, -1));
  }, []);

  const handleClearConfirm = useCallback((): void => {
    setVertices([]);
    setMode('idle');
    enablePan(true);
    setDeleteDialogOpen(false);
    setSaveError(null);
  }, [enablePan]);

  // Phase 8 / Task 8.2 (DEC-080) — unified save. `isVerified` true → "Verify &
  // Save"; false → "Save Draft". Branches on the current geometry mode.
  const doSave = useCallback(
    (isVerified: boolean): void => {
      if (!siteId) return;
      setSaveError(null);
      setSaveSuccess(false);

      let body: SaveGeofenceBody;
      if (geometryModeRef.current === 'POLYGON') {
        if (verticesRef.current.length < 3) {
          setSaveError('Draw a polygon first (at least 3 points).');
          return;
        }
        const json = featureToString(toClosedRingFeature(verticesRef.current));
        if (!isClosedRingPolygon(json)) {
          setSaveError('Geofence could not be saved. Your edits have been preserved — try again.');
          return;
        }
        body = { siteType, geofenceType: 'POLYGON', polygonGeojson: json, isVerified };
      } else {
        const center = pinRef.current;
        if (!center) {
          setSaveError('Place the pin on the tank battery first.');
          return;
        }
        body = {
          siteType,
          geofenceType: 'RADIUS',
          radiusFt: radiusFtRef.current,
          // Disposal RADIUS center stays the facility's own center server-side
          // (the shared catalog is not mutated per-tenant); these are sent for
          // PICKUP pin-move only and ignored by the server for DISPOSAL.
          centerLng: center[0],
          centerLat: center[1],
          isVerified,
        };
      }

      saveGeofence(body, {
        onSuccess: () => {
          setSaveSuccess(true);
          setSaveError(null);
          setMode('idle');
          enablePan(true);
        },
        onError: (err: Error) => {
          // Quick 260615-ni0 (#1) — geofence editing requires EDIT_GEOFENCE
          // (DISPATCH_MANAGER+). Surface that clearly instead of a generic error.
          if (err.message.includes('403')) {
            setSaveError('Geofence editing requires the Dispatch Manager role.');
          } else {
            setSaveError(
              'Geofence could not be saved. Your edits have been preserved — try again.',
            );
          }
        },
      });
    },
    [siteId, siteType, saveGeofence, enablePan],
  );

  const handleVerifySave = useCallback((): void => doSave(true), [doSave]);
  const handleSaveDraft = useCallback((): void => doSave(false), [doSave]);
  // "Skip" — leave the site unverified; nothing is saved.
  const handleSkip = useCallback((): void => {
    setSaveError(null);
    setSaveSuccess(false);
  }, []);

  const handleSwitchToPolygon = useCallback((): void => {
    setGeometryMode('POLYGON');
    setVertices([]);
    setMode('idle');
  }, []);
  const handleSwitchToRadius = useCallback((): void => {
    setGeometryMode('RADIUS');
    setMode('idle');
    enablePan(true);
  }, [enablePan]);

  const drawing = mode === 'drawing';

  return (
    <div
      style={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column' }}
      data-testid="geofence-editor"
    >
      {/* Map container — ALWAYS mounted. Quick 260616-gfx (#1): previously this
          lived inside the `siteId` branch, so when the Geofences screen first
          mounted with no site selected the container was absent and the once-run
          attach effect (deps [acquire, release]) early-returned without calling
          acquire(); selecting a site afterwards re-rendered but did NOT re-run the
          effect, so the shared map was never teleported onto this surface. Keeping
          it mounted lets the effect acquire the map on first mount; the "select a
          site" hint is an overlay on top until a site is chosen. */}
      <div
        ref={containerRef}
        style={{ flex: 1, width: '100%', height: '100%' }}
        data-testid="mapbox-satellite-container"
      />

      {siteId === null && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            backgroundColor: 'var(--color-bg-primary)',
            color: 'var(--color-text-tertiary)',
            fontSize: '14px',
            fontFamily: 'var(--font-sans, system-ui, sans-serif)',
          }}
        >
          <p>Select a site to edit its geofence</p>
        </div>
      )}

      {siteId !== null && (
        <>
          {/* Polygon toolbar — only in POLYGON mode (Phase 8 / Task 8.2) */}
          {geometryMode === 'POLYGON' && (
            <div
              style={{
                position: 'absolute',
                top: '12px',
                left: '12px',
                zIndex: 10,
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                backgroundColor: 'var(--color-bg-secondary)',
                border: '1px solid var(--color-border-primary)',
                borderRadius: '6px',
                padding: '4px',
              }}
            >
              <button
                type="button"
                aria-label="Draw polygon"
                aria-pressed={drawing}
                title="Draw polygon"
                onClick={handleStartDraw}
                style={
                  drawing
                    ? {
                        ...toolbarBtnStyle,
                        backgroundColor: 'var(--color-accent-blue)',
                        color: 'var(--color-on-accent)',
                      }
                    : toolbarBtnStyle
                }
              >
                <Pen size={16} />
              </button>
              <button
                type="button"
                aria-label="Close polygon"
                title="Close polygon"
                disabled={!drawing || vertices.length < 3}
                onClick={closePolygon}
                style={{
                  ...toolbarBtnStyle,
                  opacity: !drawing || vertices.length < 3 ? 0.4 : 1,
                  color: 'var(--color-success)',
                }}
              >
                <Check size={16} />
              </button>
              <button
                type="button"
                aria-label="Undo last point"
                title="Undo last point"
                disabled={vertices.length === 0}
                onClick={handleUndo}
                style={{ ...toolbarBtnStyle, opacity: vertices.length === 0 ? 0.4 : 1 }}
              >
                <Undo2 size={16} />
              </button>
              <button
                type="button"
                aria-label="Clear polygon"
                title="Clear polygon"
                onClick={() => {
                  setDeleteDialogOpen(true);
                }}
                style={{ ...toolbarBtnStyle, color: 'var(--color-danger)' }}
              >
                <Trash2 size={16} />
              </button>
            </div>
          )}

          {/* Base-style switcher — top-right (#4) */}
          <div
            role="group"
            aria-label="Map style"
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              zIndex: 10,
              display: 'flex',
              backgroundColor: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border-primary)',
              borderRadius: '4px',
              overflow: 'hidden',
            }}
          >
            {MAP_STYLES.map((s) => {
              const active = s.key === styleKey;
              return (
                <button
                  key={s.key}
                  type="button"
                  aria-pressed={active}
                  onClick={(): void => {
                    setStyleKey(s.key);
                  }}
                  style={{
                    padding: '5px 8px',
                    fontSize: '11px',
                    fontFamily: 'var(--font-sans, DM Sans, system-ui, sans-serif)',
                    fontWeight: 600,
                    border: 'none',
                    cursor: 'pointer',
                    backgroundColor: active ? 'var(--color-accent-blue)' : 'transparent',
                    color: active ? 'var(--color-on-accent)' : 'var(--color-text-secondary)',
                  }}
                >
                  {s.label}
                </button>
              );
            })}
          </div>

          {/* Drawing hint */}
          {drawing && (
            <div
              style={{
                position: 'absolute',
                top: '12px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 10,
                padding: '6px 12px',
                fontSize: '12px',
                color: 'var(--color-on-accent)',
                backgroundColor: 'var(--color-accent-blue)',
                borderRadius: '4px',
              }}
            >
              Click to add points · click the first point or “Close polygon” to finish
            </div>
          )}

          {/* Live coordinate readout — bottom-left (#4) */}
          {coords && (
            <div
              style={{
                position: 'absolute',
                bottom: '8px',
                left: '8px',
                zIndex: 10,
                padding: '3px 8px',
                fontSize: '12px',
                fontFamily: 'var(--font-mono, JetBrains Mono, monospace)',
                color: 'var(--color-text-secondary)',
                backgroundColor: 'var(--color-bg-secondary)',
                border: '1px solid var(--color-border-primary)',
                borderRadius: '4px',
                pointerEvents: 'none',
              }}
            >
              {formatNumber(coords.lat, { decimals: 5 })},{' '}
              {formatNumber(coords.lng, { decimals: 5 })}
            </div>
          )}

          {drawError && (
            <div
              role="alert"
              style={{
                position: 'absolute',
                top: '52px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 11,
                maxWidth: '420px',
                padding: '6px 12px',
                fontSize: '12px',
                textAlign: 'center',
                color: 'var(--color-on-accent)',
                backgroundColor: 'var(--color-danger)',
                borderRadius: '4px',
              }}
            >
              {drawError}
            </div>
          )}

          {/* Save controls — bottom overlay */}
          <div
            style={{
              position: 'absolute',
              bottom: '16px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 10,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: 'var(--color-bg-secondary)',
              borderRadius: 'var(--radius-card)',
              padding: '12px 16px',
            }}
          >
            {saveError && (
              <p
                role="alert"
                style={{
                  fontSize: '13px',
                  color: 'var(--color-danger)',
                  maxWidth: '360px',
                  textAlign: 'center',
                  margin: 0,
                }}
              >
                {saveError}
              </p>
            )}
            {saveSuccess && (
              <p
                role="status"
                style={{ fontSize: '13px', color: 'var(--color-success)', margin: 0 }}
              >
                Geofence saved successfully.
              </p>
            )}

            {/* Phase 8 / Task 8.2 — geometry-mode toggle */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {geometryMode === 'POLYGON' ? (
                <Button variant="outline" size="sm" onClick={handleSwitchToRadius}>
                  Switch to radius
                </Button>
              ) : (
                <Button variant="outline" size="sm" onClick={handleSwitchToPolygon}>
                  Switch to polygon
                </Button>
              )}
            </div>

            {/* RADIUS mode — radius slider */}
            {geometryMode === 'RADIUS' && (
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '260px' }}
              >
                <label
                  htmlFor="geofence-radius"
                  style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}
                >
                  Radius: {radiusFt} ft
                </label>
                <input
                  id="geofence-radius"
                  type="range"
                  min={MIN_RADIUS_FT}
                  max={MAX_RADIUS_FT}
                  step={10}
                  value={radiusFt}
                  aria-label="Geofence radius (feet)"
                  onChange={(e): void => {
                    setRadiusFt(Number(e.target.value));
                  }}
                />
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Button
                variant="default"
                onClick={handleVerifySave}
                disabled={isSaving}
                style={{
                  backgroundColor: 'var(--color-accent-blue)',
                  color: 'var(--color-on-accent)',
                  borderColor: 'var(--color-accent-blue)',
                }}
              >
                Verify &amp; Save
              </Button>
              <Button
                variant="outline"
                onClick={handleSaveDraft}
                disabled={isSaving}
                style={{
                  borderColor: 'var(--color-accent-blue)',
                  color: 'var(--color-accent-blue)',
                }}
              >
                Save Draft
              </Button>
              <Button variant="ghost" onClick={handleSkip} disabled={isSaving}>
                Skip
              </Button>
            </div>
          </div>

          <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Clear geofence for {siteName ?? 'this site'}?</DialogTitle>
              </DialogHeader>
              <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>
                This removes the drawn polygon from the editor. Nothing is saved until you click
                Verify &amp; Save.
              </p>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setDeleteDialogOpen(false);
                  }}
                >
                  Keep
                </Button>
                <Button variant="destructive" onClick={handleClearConfirm}>
                  Clear
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}

const toolbarBtnStyle: React.CSSProperties = {
  width: '32px',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: 'var(--radius-pill)',
  color: 'var(--color-text-primary)',
  cursor: 'pointer',
};
